// Mesure de similarité inter-projets — portage de poc/src/similarite.js.
//
// La référence reste poc/scripts/similarite.py ; tests/parite-poc.test.ts vérifie
// que ce portage rend exactement les mêmes scores que le POC, paire par paire.
//
// En deux phrases (poc/docs/vues/specification.md § 2.1–2.2) : les leviers seuls
// donnent une mesure dégénérée (beaucoup de profils identiques), donc la mesure est
// composite ; et le plan d'action est volontairement exclu du calcul pour servir
// de témoin.
//
// ⚠ La pondération est un jugement métier NON VALIDÉ par un agronome. L'interface
// doit le dire partout où un score est affiché.

import type { Dimensions, Projet } from "./types";

export type DimensionSimilarite = "leviers" | "cultures" | "bioagresseurs";

export type Poids = Record<DimensionSimilarite, number>;

/** Pondération de départ du POC, en attente de validation. */
export const POIDS_PAR_DEFAUT: Readonly<Poids> = Object.freeze({
  leviers: 0.47,
  cultures: 0.29,
  bioagresseurs: 0.24,
});

const DIMENSIONS: DimensionSimilarite[] = ["leviers", "cultures", "bioagresseurs"];

export function jaccard<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): number {
  if (!a.size && !b.size) return 0;
  let commun = 0;
  a.forEach((v) => {
    if (b.has(v)) commun++;
  });
  return commun / (a.size + b.size - commun);
}

const ensembles = (p: Projet): Record<DimensionSimilarite, Set<string>> => ({
  leviers: new Set(p.leviers),
  cultures: new Set(p.cultures.map((c) => c.cle)),
  bioagresseurs: new Set(p.bioagresseurs),
});

export interface Voisin {
  /** Indice du projet voisin dans le tableau de projets. */
  autre: number;
  score: number;
  /** Ressemblance élémentaire par dimension, avant pondération. */
  detail: Partial<Record<DimensionSimilarite, number>>;
}

export interface Similarites {
  /** Poids effectivement appliqués, renormalisés sur les dimensions présentes. */
  poids: Partial<Poids>;
  dimensionsUtilisees: DimensionSimilarite[];
  /** Pour chaque projet, les autres projets du plus proche au plus lointain. */
  voisins: Voisin[][];
}

/**
 * Calcule les similarités de toutes les paires.
 *
 * Les poids sont renormalisés sur les dimensions que la donnée alimente : sans
 * cultures, retirer la dimension vaut mieux que la compter à zéro en silence.
 */
export function calculerSimilarites(
  projets: Projet[],
  dimensions: Dimensions,
  poidsBruts: Poids = POIDS_PAR_DEFAUT,
): Similarites {
  const actives = DIMENSIONS.filter((d) => dimensions[d]);
  const somme = actives.reduce((t, d) => t + poidsBruts[d], 0);
  const poids: Partial<Poids> = {};
  // Une pondération entièrement nulle n'a pas de sens : on retombe sur l'égalité.
  for (const d of actives) poids[d] = somme > 0 ? poidsBruts[d] / somme : 1 / actives.length;

  const ens = projets.map(ensembles);
  const voisins: Voisin[][] = projets.map(() => []);
  ens.forEach((a, i) => {
    ens.slice(i + 1).forEach((b, k) => {
      const j = i + 1 + k;
      const detail: Voisin["detail"] = {};
      let score = 0;
      for (const d of actives) {
        const valeur = jaccard(a[d], b[d]);
        detail[d] = valeur;
        score += (poids[d] ?? 0) * valeur;
      }
      voisins[i]?.push({ autre: j, score, detail });
      voisins[j]?.push({ autre: i, score, detail });
    });
  });
  for (const v of voisins) v.sort((x, y) => y.score - x.score);
  return { poids, dimensionsUtilisees: actives, voisins };
}
